import { DAY_TOKENS } from "./interviewTheme";
import { questionCardBackground, rgbaToCss, shade, surveyBackgroundStyle, withAlpha } from "../../../components/Survey/surveyStyle";

// The Modern (voice) runner's look, from the survey's own appearance settings
// (the builder's Advanced tab) — the same settings, read the same way, as the
// traditional runner. There is no day/night switch: the author picks the look.
//
// Starts from the interview's day tokens and overrides everything a setting
// covers, with the traditional runner's defaults where a setting is unset:
//   background (single / gradient / image + brightness) → the page behind it all
//   fontColour → text, progress, cues; questionFontColor → questions;
//   surveyTitleColor → the title and headings; choiceFontColor → options and the Likert scale
//   questionBackground + questionOpacity → the cards
//   back / next / submit button texts and colours → Back / Continue / Submit
//   logo + logoAlignment, showProgressBar, showQuestionNumber → as on the classic page
// and the Modern-only ones (the Advanced tab shows them for a Modern survey):
//   recordButtonText / BackgroundColor / FontColor → the Record button
//   voiceOrbColor → the orb (shades of it), else the interview's aurora
export function surveyInterviewTheme(settings = {}) {
    const font = rgbaToCss(settings.fontColour || "#000000");
    const question = rgbaToCss(settings.questionFontColor || font);
    const choice = rgbaToCss(settings.choiceFontColor || font);
    const titleColor = rgbaToCss(settings.surveyTitleColor || font);
    const muted = withAlpha(font, 0.72);
    const card = questionCardBackground(settings);
    const nextBg = settings.nextButtonBackgroundColor || "rgba(255,255,255,0.9)";
    const nextText = settings.nextButtonFontColor || "rgba(0,0,0,0.8)";
    const submitBg = settings.submitButtonBackgroundColor || nextBg;
    const submitText = settings.submitButtonFontColor || nextText;
    const backBg = settings.backButtonBackgroundColor || "rgba(255,255,255,0.9)";
    const backText = settings.backButtonFontColor || "rgba(0,0,0,0.8)";
    const orb = settings.voiceOrbColor;

    return {
        ...DAY_TOKENS,
        mode: "survey",
        // The runner paints this behind every screen; the screens stay transparent.
        pageBackground: surveyBackgroundStyle(settings),
        screenMinHeight: "100%",
        logo: settings.logo || null,
        logoAlignment: (settings.logoAlignment || "left").toLowerCase(),
        showProgress: !!settings.showProgressBar,
        showQuestionNumber: !!settings.showQuestionNumber,

        // Text
        question,
        heading: titleColor,
        formSerif: question,
        formLabel: question,
        panelText: question,
        say: muted,
        kicker: titleColor,
        panelMuted: muted,
        formMuted: muted,

        // Progress, the turn cue and the content Listen pill
        progressTrack: withAlpha(font, 0.18),
        progressFill: font,
        pct: font,
        teal: font,
        cueBg: withAlpha(font, 0.1),
        cueText: font,
        progressGlow: "none",

        // Chrome: no frosted bars or sheet — the cards sit on the survey's background
        topBarBg: "transparent",
        topBarBorder: "transparent",
        topBarShadow: "none",
        footerBg: "transparent",
        footerBorder: "transparent",
        sheetBg: "transparent",
        sheetBgSolid: "transparent",
        sheetBlur: "none",
        sheetBorder: "transparent",
        sheetShadow: "none",
        pauseBorder: withAlpha(font, 0.35),
        pauseBg: "transparent",
        pauseText: font,

        // Cards, as the classic page draws them
        formCardBg: card,
        formCardBgSolid: card,
        formCardBlur: "none",
        formCardBorder: "transparent",
        formCardShadow: "0 4px 20px rgba(0,0,0,0.1)",
        panelBg: card,
        panelBorder: withAlpha(font, 0.35),
        panelDashed: withAlpha(font, 0.3),
        // Answer fields take whatever is behind them
        fieldBg: "transparent",
        badgeBg: withAlpha(question, 0.1),
        badgeText: question,

        // Options
        optBorder: withAlpha(choice, 0.25),
        optBg: "transparent",
        optText: choice,
        optHoverBorder: choice,
        optHoverBg: withAlpha(choice, 0.04),
        optSelBorder: choice,
        optSelBg: withAlpha(choice, 0.08),
        optSelText: choice,
        radioBorder: withAlpha(choice, 0.5),
        radioFill: choice,
        scaleNum: choice,
        scaleLabel: withAlpha(choice, 0.72),
        scaleSelBorder: choice,
        scaleSelBg: withAlpha(choice, 0.16),
        scaleSelText: choice,

        // Continue (the classic Next) and the final Submit
        formBtnBg: nextBg,
        formBtnHover: nextBg,
        formBtnText: nextText,
        formBtnShadow: "0 4px 14px rgba(0,0,0,0.12)",
        submitBg: nextBg,
        submitHover: nextBg,
        submitText: nextText,
        ctaShadow: "0 4px 14px rgba(0,0,0,0.12)",
        finalBtnBg: submitBg,
        finalBtnText: submitText,
        backBtnBg: backBg,
        backBtnText: backText,
        recordBtnBg: settings.recordButtonBackgroundColor || "#128049",
        recordBtnText: settings.recordButtonFontColor || "#ffffff",
        orbPalette: orb
            ? { navy: shade(orb, -0.45), sky: rgbaToCss(orb), aqua: shade(orb, 0.3), teal: shade(orb, -0.15), light: shade(orb, 0.55) }
            : undefined,
    };
}
